import React, { useRef, useState } from "react";
import cx from "classnames";
import { motion, AnimatePresence } from "motion/react";
import { Pill, RadioButton, RoundButton, Text } from "@foris/avocado-suite";
import useClickOutside from "@common/hooks/useClickOutside";
import styles from "./autocompleteSelector.module.scss";
import { useTranslation } from "react-i18next";

type SearchOption = {
  label: string;
  value: string;
};

interface AutocompleteSelectorProps {
  initialOption?: SearchOption;
  initialInputValue?: string;
  options?: Array<SearchOption>;
  isLoading?: boolean;
  className?: string;
  isClearable?: boolean;
  onSearch?: (payload: { query: string; option: SearchOption }) => void;
  onClearSearch?: () => void;
}

const AutocompleteSelector = ({
  initialOption = null,
  initialInputValue = "",
  options = [],
  isLoading,
  className = "",
  isClearable = true,
  onSearch,
  onClearSearch,
}: AutocompleteSelectorProps) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [inputValue, setInputValue] = useState(initialInputValue);
  const [showOptions, setShowOptions] = useState(false);
  const [selectedOption, setSelectedOption] = useState(initialOption);

  useClickOutside({
    ref,
    onClickOutside: () => setShowOptions(false),
  });

  const handleSearchRequest = () => {
    if (selectedOption && onSearch) {
      onSearch({ query: inputValue, option: selectedOption });
    }
  };

  return (
    <div ref={ref} className={cx(styles.autocompleteSelector, className)}>
      <div
        className={cx(
          styles.autocompleteSelector_control,
          (!selectedOption || isLoading) && styles.autocompleteSelector_control__disabled,
          !selectedOption && !showOptions && styles.autocompleteSelector_control__empty,
        )}
        role="button"
        onClick={() => !selectedOption && !isLoading && setShowOptions(!showOptions)}
      >
        <AnimatePresence mode="wait">
          {!!selectedOption && (
            <motion.div
              layout
              key={selectedOption.value}
              initial={{ opacity: 0, scale: 0.65 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.65 }}
              transition={{
                duration: 0.25,
                ease: [0, 0.71, 0.2, 1.01],
              }}
              className={styles.controlPill}
              role="button"
              onClick={() => setShowOptions(!showOptions)}
            >
              <Pill className={styles.controlPill_pill} color="primary-20" textColor="neutral-80">
                {selectedOption?.label}
              </Pill>
            </motion.div>
          )}
        </AnimatePresence>
        <input
          className={styles.controlField}
          placeholder={t("autocomplete-search.placeholder")}
          disabled={!selectedOption || isLoading}
          value={inputValue}
          onChange={e => setInputValue(e?.target?.value?.trim())}
          onKeyUp={e => {
            if (e?.key === "Enter" && !isLoading && !!inputValue && !!selectedOption) {
              handleSearchRequest();
            }
          }}
        />
        <div className={styles.controlSearch}>
          {selectedOption && isClearable && (
            <RoundButton
              icon="close"
              variant="ghost"
              size="sm"
              disabled={!selectedOption || isLoading}
              onClick={() => {
                setSelectedOption(null);
                setInputValue("");
                onClearSearch?.();
              }}
            />
          )}

          <RoundButton
            icon="search"
            variant="ghost"
            disabled={!selectedOption || isLoading || !inputValue}
            onClick={handleSearchRequest}
          />
        </div>
      </div>

      <AnimatePresence>
        {showOptions && !isLoading && (
          <motion.div
            className={styles.autocompleteSelector_options}
            style={{ zIndex: 10, transformOrigin: "top" }}
            initial={{ scaleY: 0, opacity: 0 }}
            animate={{ scaleY: 1, opacity: 1 }}
            exit={{ scaleY: 0, opacity: 0 }}
            transition={{
              scaleY: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
              opacity: { duration: 0.2, ease: "easeInOut" },
            }}
          >
            <Text className={styles.optionsTitle} type="sm">
              Buscar en
            </Text>

            <div className={styles.optionsList}>
              {options?.map(option => (
                <div
                  key={option?.value}
                  className={cx(
                    styles.optionItem,
                    selectedOption?.value === option?.value && styles.optionItem__selected,
                  )}
                >
                  <RadioButton
                    className={styles.optionItem_text}
                    labelRight={option?.label}
                    checked={selectedOption?.value === option?.value}
                    onChange={() => {
                      setSelectedOption(option);
                      setShowOptions(false);
                    }}
                  />
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AutocompleteSelector;
